import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  ArrowLeft,
  FileText, 
  DollarSign, 
  Download, 
  Calculator,
  PieChart,
  Receipt,
  BarChart3,
  FileSpreadsheet,
  User,
  LogOut,
  Crown,
  Shield,
  Menu,
  Clock,
  CheckCircle,
  Search,
  Filter,
  Calendar,
  Trash2,
  AlertTriangle,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  Combine,
  CheckSquare,
  Undo2,
  Sparkles,
  Layers
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart as RechartsPieChart,
  Cell,
  ResponsiveContainer,
  LineChart,
  Line,
  Area,
  AreaChart
} from "recharts";
import { toast } from "sonner";
import { AuthContext } from "../App";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

/**
 * Modal asking the user if they'd like to save a rule so future statements
 * automatically classify matching transactions the same way.
 * Pre-fills a suggested keyword per source description; user can edit/remove
 * keywords before saving.
 */
function RememberMoveDialog({ pending, allCategories, onCancel, onDontAsk, onSave }) {
  const initial = (pending.suggestedKeywords || []).map((kw, i) => ({
    id: i,
    keyword: kw,
    sources: pending.descriptions || [],
  }));
  const [keywords, setKeywords] = React.useState(initial);
  const categoryLabel = (() => {
    const c = allCategories.find(c => c.key === pending.category);
    return c ? c.label : pending.category.replace(/_/g, ' ');
  })();

  const setKw = (id, value) => {
    setKeywords(prev => prev.map(k => k.id === id ? { ...k, keyword: value } : k));
  };
  const removeKw = (id) => setKeywords(prev => prev.filter(k => k.id !== id));

  const handleSave = () => {
    const rules = keywords
      .map(k => ({
        keyword: k.keyword.trim(),
        category: pending.category,
        source_descriptions: pending.descriptions,
      }))
      .filter(r => r.keyword.length >= 2);
    onSave(rules);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="remember-move-dialog">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-indigo-600" />
            Remember this for future statements?
          </CardTitle>
          <CardDescription>
            AI Tax Helper can auto-tag matching transactions in future uploads as <span className="font-medium text-slate-900">{categoryLabel}</span>.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <p className="text-xs font-medium text-slate-700 mb-1.5">Match transactions whose description contains any of:</p>
            <div className="space-y-2">
              {keywords.length === 0 && (
                <p className="text-sm text-slate-500 italic">No keywords — add one or skip.</p>
              )}
              {keywords.map(k => (
                <div key={k.id} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={k.keyword}
                    onChange={(e) => setKw(k.id, e.target.value)}
                    className="flex-1 px-3 py-1.5 text-sm rounded border border-slate-300 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    data-testid={`remember-keyword-${k.id}`}
                  />
                  <Button size="sm" variant="ghost" onClick={() => removeKw(k.id)} className="px-2">
                    <Trash2 className="w-4 h-4 text-slate-400" />
                  </Button>
                </div>
              ))}
            </div>
            <p className="mt-2 text-xs text-slate-500">
              Tip: keep keywords short (e.g. "mcdonalds" instead of the full line) so they match variations.
            </p>
          </div>
          {pending.descriptions && pending.descriptions.length > 0 && (
            <div className="text-xs text-slate-500 max-h-24 overflow-y-auto rounded bg-slate-50 p-2 border border-slate-200">
              <p className="font-medium text-slate-600 mb-1">Based on:</p>
              <ul className="space-y-0.5">
                {pending.descriptions.slice(0, 5).map((d, i) => (
                  <li key={i} className="truncate">• {d}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
            <button
              type="button"
              onClick={onDontAsk}
              className="text-xs text-slate-500 hover:text-slate-700 underline underline-offset-2"
              data-testid="remember-dont-ask"
            >
              Don't ask again this session
            </button>
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" size="sm" onClick={onCancel} data-testid="remember-not-now">
                Not now
              </Button>
              <Button size="sm" onClick={handleSave} disabled={keywords.length === 0} data-testid="remember-save">
                Remember
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


function PreviousStatements() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [selectedStatement, setSelectedStatement] = useState(null);
  const [taxSummary, setTaxSummary] = useState(null);
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("date_desc");
  const [deleteConfirm, setDeleteConfirm] = useState(null); // For single statement delete
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false); // For delete all confirmation
  const [expandedCategories, setExpandedCategories] = useState({}); // Track expanded expense categories
  const [selectedIds, setSelectedIds] = useState(new Set()); // Track selected statements for merge
  const [merging, setMerging] = useState(false); // Loading state for merge operations
  const [mappedKeys, setMappedKeys] = useState(null); // Set of internal category keys covered by an export bucket
  const [allCategories, setAllCategories] = useState([]); // Every known IRS category for drop targets
  const [suggestionCount, setSuggestionCount] = useState(0); // Pending Suggested Rules count for nav badge
  const [selectedTxIds, setSelectedTxIds] = useState(new Set()); // Cross-category checkbox selection
  const [recategorizing, setRecategorizing] = useState(false); // Loading state during PATCH
  const [bulkMoveCategory, setBulkMoveCategory] = useState(''); // Dropdown selection for bulk move
  const [draggedTxIds, setDraggedTxIds] = useState(null); // [id, ...] currently being dragged
  const [dropTargetCategory, setDropTargetCategory] = useState(null); // Highlight target during drag
  const [moveHistory, setMoveHistory] = useState([]); // Stack of recent moves for Undo. Most recent at end.
  const [pendingRemember, setPendingRemember] = useState(null); // {ids, descriptions, suggestedKeywords, category} for the "Remember?" dialog
  const [dontAskRemember, setDontAskRemember] = useState(false); // Session-only opt-out

  // Compute uncategorized review stats for a statement.
  // Returns { count, total } for business-expense txns whose category isn't
  // mapped to any explicit display bucket (catch-all "Uncategorized" rows).
  const getUncategorizedStats = (statement) => {
    if (!mappedKeys || !statement?.transactions) return { count: 0, total: 0 };
    let count = 0;
    let total = 0;
    for (const tx of statement.transactions) {
      if (tx.is_business_expense && !mappedKeys.has(tx.category)) {
        count += 1;
        total += Number(tx.amount) || 0;
      }
    }
    return { count, total };
  };

  // Toggle category expansion
  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Filter transactions by category
  const getTransactionsByCategory = (category) => {
    if (!selectedStatement || !selectedStatement.transactions) return [];
    return selectedStatement.transactions.filter(tx => 
      tx.category === category && tx.is_business_expense
    );
  };

  const loadStatements = async () => {
    try {
      console.group('loadStatements - Loading previous statements');
      const token = localStorage.getItem('token');
      console.log('Token status:', token ? `Token exists (${token.substring(0, 20)}...)` : 'No token');
      console.log('API URL:', `${API}/tax-helper/statements`);
      
      const response = await axios.get(`${API}/tax-helper/statements`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      console.log('✅ API Response received successfully');
      console.log('Response status:', response.status);
      console.log('Response data length:', response.data?.length);
      
      setStatements(response.data || []);
      console.log('✅ Statements loaded:', response.data?.length || 0);
      console.groupEnd();
      
    } catch (error) {
      console.error('❌ Error loading statements:', error);
      console.error('API URL:', `${API}/tax-helper/statements`);
      console.error('Error status:', error.response?.status);
      console.error('Error response data:', error.response?.data);
      console.error('Error message:', error.message);
      console.groupEnd();
      toast.error("Failed to load previous statements");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && user.id) {
      console.log('Loading previous statements for user:', user.id);
      loadStatements();
    }
  }, [user?.id]);

  // Load the set of mapped category keys once so we can compute the
  // "uncategorized review" widget on each statement card.
  useEffect(() => {
    if (!user?.id) return;
    const token = localStorage.getItem('token');
    axios.get(`${API}/tax-helper/category-buckets/mapped-keys`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => {
      setMappedKeys(new Set(res.data?.mapped_keys || []));
    }).catch(err => {
      console.error('Failed to load mapped category keys:', err);
    });

    // Load every known IRS category for the drop-target list and bulk-move dropdown.
    axios.get(`${API}/tax-helper/category-buckets/all-categories`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => {
      setAllCategories(res.data?.categories || []);
    }).catch(err => {
      console.error('Failed to load all categories:', err);
    });

    // Suggestion count for nav badge
    axios.get(`${API}/tax-helper/user-rules/suggestions`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => {
      setSuggestionCount((res.data?.suggestions || []).length);
    }).catch(() => { /* ignore — badge stays 0 */ });
  }, [user?.id]);

  // Toggle a single transaction's checkbox selection.
  const toggleTxSelection = (txId) => {
    setSelectedTxIds(prev => {
      const next = new Set(prev);
      if (next.has(txId)) next.delete(txId);
      else next.add(txId);
      return next;
    });
  };

  // Derive a keyword candidate from a transaction description.
  // Mirrors backend services/user_rules.extract_keyword (loose copy).
  const extractKeyword = (description) => {
    if (!description) return '';
    const stop = new Set(['pos','purchase','debit','card','visa','mastercard','transaction','authorized','pending','autopay','auto','pay','payment','recurring','online','mobile','check','deposit','withdrawal','transfer','from','to','the','inc','llc','co','corp','company','on','at','in','ref','id','txn','sq','square']);
    const cleaned = description.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    for (const tok of cleaned.split(' ')) {
      if (!tok || /^\d+$/.test(tok) || stop.has(tok) || tok.length < 3) continue;
      return tok;
    }
    return cleaned.slice(0, 40);
  };

  // Apply a category change to a list of transaction ids.
  // When `skipHistory` is true, the move is not pushed onto the undo stack
  // (used internally by Undo so undo-of-undo doesn't happen).
  const applyRecategorize = async (txIds, newCategory, { skipHistory = false, skipRememberPrompt = false } = {}) => {
    if (!selectedStatement || !txIds.length || !newCategory) return;
    setRecategorizing(true);
    try {
      const token = localStorage.getItem('token');
      // Capture previous state for undo BEFORE the mutation.
      const prevByTxId = {};
      for (const tx of (selectedStatement.transactions || [])) {
        if (txIds.includes(tx.id)) {
          prevByTxId[tx.id] = { category: tx.category, description: tx.description };
        }
      }
      const updates = txIds.map(id => ({ transaction_id: id, new_category: newCategory }));
      const res = await axios.patch(
        `${API}/tax-helper/statements/${selectedStatement.id}/recategorize`,
        { updates },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSelectedStatement(res.data.statement);
      setTaxSummary(res.data.tax_summary);
      setStatements(prev => prev.map(s => s.id === res.data.statement.id ? res.data.statement : s));
      setSelectedTxIds(new Set());
      setBulkMoveCategory('');
      toast.success(`Moved ${res.data.matched} transaction${res.data.matched === 1 ? '' : 's'} to ${newCategory.replace(/_/g, ' ')}`);

      if (!skipHistory) {
        setMoveHistory(prev => {
          const next = [...prev, {
            id: Date.now() + Math.random(),
            timestamp: new Date().toISOString(),
            txIds: [...txIds],
            newCategory,
            previousByTxId: prevByTxId,
          }];
          return next.slice(-15); // keep last 15
        });
      }

      // Offer to remember the move (only on genuine user moves, not undos)
      if (!skipHistory && !skipRememberPrompt && !dontAskRemember) {
        const descriptions = Object.values(prevByTxId).map(p => p.description).filter(Boolean);
        if (descriptions.length) {
          const suggestedKeywords = Array.from(new Set(descriptions.map(extractKeyword).filter(Boolean)));
          setPendingRemember({
            txIds: [...txIds],
            descriptions,
            suggestedKeywords,
            category: newCategory,
          });
        }
      }
    } catch (err) {
      console.error('Recategorize failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to move transactions');
    } finally {
      setRecategorizing(false);
    }
  };

  // Undo the most recent move by re-applying each tx's previous category.
  const undoLastMove = async () => {
    if (!moveHistory.length || !selectedStatement) return;
    const last = moveHistory[moveHistory.length - 1];
    // Group txIds by previous category for minimal PATCH calls
    const byPrevCat = {};
    for (const [txId, prev] of Object.entries(last.previousByTxId)) {
      const cat = prev.category || 'other_expenses';
      if (!byPrevCat[cat]) byPrevCat[cat] = [];
      byPrevCat[cat].push(txId);
    }
    // Remove the entry from history immediately (optimistic)
    setMoveHistory(prev => prev.slice(0, -1));
    // Issue the reverse PATCH calls sequentially
    for (const [cat, ids] of Object.entries(byPrevCat)) {
      await applyRecategorize(ids, cat, { skipHistory: true, skipRememberPrompt: true });
    }
    toast.success('Undone');
  };

  // Persist one or more learned rules to the backend.
  const saveRememberedRules = async (rules) => {
    if (!rules?.length) {
      setPendingRemember(null);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/tax-helper/user-rules`,
        { rules },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Saved ${res.data.saved} rule${res.data.saved === 1 ? '' : 's'}. Future uploads will auto-categorize matching transactions.`);
    } catch (err) {
      console.error('Failed to save rules:', err);
      toast.error(err.response?.data?.detail || 'Failed to save rule(s)');
    } finally {
      setPendingRemember(null);
    }
  };

  const handleSelectStatement = async (statement) => {
    setSelectedStatement(statement);
    setExpandedCategories({}); // Reset expanded categories when switching statements
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/tax-helper/tax-summary/${statement.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      setTaxSummary(response.data);
      toast.success("Statement details loaded successfully!");
    } catch (error) {
      console.error('Error loading tax summary:', error);
      toast.error("Failed to load statement details");
    }
  };

  const handleExportExcel = async (statementId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/tax-helper/export-excel/${statementId}`, {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tax_analysis_${statementId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Excel file downloaded successfully!");
    } catch (error) {
      console.error('Export error:', error);
      toast.error("Failed to export Excel file");
    }
  };

  const handleExportGoogleSheets = (statementId) => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error("Please log in to export to Google Sheets");
      return;
    }
    const authUrl = `${API}/tax-helper/google-sheets/auth/${statementId}?token=${token}`;
    window.open(authUrl, '_blank');
    toast.success("Opening Google Sheets... Please authorize if prompted.");
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // ── Selection / Merge handlers ──
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredAndSortedStatements.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredAndSortedStatements.map(s => s.id)));
    }
  };

  const handleMergeExcel = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length < 2) { toast.error("Select at least 2 statements to merge"); return; }
    setMerging(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/tax-helper/export-merged-excel`,
        { statement_ids: ids },
        { responseType: 'blob', headers: { 'Authorization': `Bearer ${token}` } },
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `merged_tax_analysis_${ids.length}_statements.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Merged Excel with ${ids.length} statements downloaded!`);
    } catch (error) {
      console.error('Merge Excel error:', error);
      toast.error("Failed to create merged Excel");
    } finally {
      setMerging(false);
    }
  };

  const [saveMergeName, setSaveMergeName] = useState(""); // Modal input for "Save as YTD"
  const [savingMerge, setSavingMerge] = useState(false);
  const [showSaveMergeDialog, setShowSaveMergeDialog] = useState(false);
  const [groupIdentical, setGroupIdentical] = useState(() => {
    try {
      return localStorage.getItem('thp:groupIdentical') === '1';
    } catch (e) {
      return false;
    }
  });

  // Persist the toggle preference across sessions / statements.
  useEffect(() => {
    try {
      localStorage.setItem('thp:groupIdentical', groupIdentical ? '1' : '0');
    } catch (e) { /* ignore quota / privacy mode */ }
  }, [groupIdentical]);

  // Group txns by case-insensitive description inside a bucket. Mirrors the
  // backend export helper but works on plain JS objects from the API.
  const groupIdenticalTxns = (txns) => {
    const map = new Map();
    const order = [];
    for (const t of txns) {
      const key = (t.description || '').trim().toLowerCase();
      if (!map.has(key)) {
        order.push(key);
        map.set(key, { description: t.description, dates: [], amount: 0, ids: [] });
      }
      const g = map.get(key);
      g.dates.push(t.date || '');
      g.amount += Number(t.amount) || 0;
      g.ids.push(t.id);
    }
    return order.map(k => {
      const g = map.get(k);
      const sortedDates = [...g.dates].filter(Boolean).sort();
      const count = g.ids.length;
      const dateDisplay = sortedDates.length === 0
        ? ''
        : sortedDates[0] === sortedDates[sortedDates.length - 1]
          ? sortedDates[0]
          : `${sortedDates[0]} → ${sortedDates[sortedDates.length - 1]}`;
      return {
        description: g.description,
        dateDisplay,
        amount: g.amount,
        ids: g.ids,
        count,
      };
    });
  };

  // Smart YTD presets — group statements by year + quarter so the user can
  // 1-click "Save 2024 YTD" or "Q1 2024" without manually checking boxes.
  const parseStatementYearMonth = (statement) => {
    const raw = statement?.statement_period_start || '';
    if (!raw) return { year: null, month: null };
    let m = raw.match(/(\d{4})-(\d{2})-/);  // ISO: 2024-01-15
    if (m) return { year: +m[1], month: +m[2] };
    m = raw.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})/);  // 01/15/2024
    if (m) return { year: +m[3], month: +m[1] };
    const monthNames = ['january','february','march','april','may','june','july','august','september','october','november','december'];
    const low = raw.toLowerCase();
    let month = null;
    for (let i = 0; i < monthNames.length; i++) {
      if (low.includes(monthNames[i])) { month = i + 1; break; }
    }
    const y = raw.match(/(\d{4})/);
    return { year: y ? +y[1] : null, month };
  };

  const ytdPresets = React.useMemo(() => {
    // Build a list of {label, name, ids, year, quarter (optional)}
    // by binning each statement by (year, quarter).
    const byYear = {};
    for (const s of statements) {
      const { year, month } = parseStatementYearMonth(s);
      if (!year || !month) continue;
      const quarter = Math.ceil(month / 3);
      if (!byYear[year]) byYear[year] = { all: [], quarters: { 1: [], 2: [], 3: [], 4: [] } };
      byYear[year].all.push(s.id);
      byYear[year].quarters[quarter].push(s.id);
    }
    const presets = [];
    const years = Object.keys(byYear).map(Number).sort((a, b) => b - a);
    for (const year of years) {
      const data = byYear[year];
      if (data.all.length >= 2) {
        presets.push({
          key: `ytd-${year}`,
          label: `${year} YTD`,
          subLabel: `${data.all.length} statements`,
          name: `${year} YTD`,
          ids: data.all,
        });
      }
      for (const q of [1, 2, 3, 4]) {
        const qIds = data.quarters[q];
        if (qIds.length >= 2) {
          presets.push({
            key: `q${q}-${year}`,
            label: `Q${q} ${year}`,
            subLabel: `${qIds.length} statements`,
            name: `Q${q} ${year}`,
            ids: qIds,
          });
        }
      }
    }
    return presets;
  }, [statements]);

  const applyYtdPreset = (preset) => {
    setSelectedIds(new Set(preset.ids));
    setSaveMergeName(preset.name);
    setShowSaveMergeDialog(true);
  };

  const handleSaveMergedStatement = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length < 2) { toast.error("Select at least 2 statements"); return; }
    const name = (saveMergeName || '').trim();
    if (!name) { toast.error("Give it a name first"); return; }
    setSavingMerge(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/tax-helper/merged-statements`,
        { name, statement_ids: ids },
        { headers: { 'Authorization': `Bearer ${token}` } },
      );
      toast.success(`Saved as "${name}". Find it under "View Year-to-Date Statements" on the dashboard.`);
      setShowSaveMergeDialog(false);
      setSaveMergeName("");
      setSelectedIds(new Set());
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save merged statement");
    } finally {
      setSavingMerge(false);
    }
  };

  const handleMergeGoogleSheets = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length < 2) { toast.error("Select at least 2 statements to merge"); return; }
    setMerging(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/tax-helper/google-sheets/create-merged`,
        { statement_ids: ids },
        { headers: { 'Authorization': `Bearer ${token}` } },
      );
      if (response.data.url) {
        window.open(response.data.url, '_blank');
        toast.success(`Merged Google Sheet with ${ids.length} statements created!`);
      } else if (response.data.needs_auth) {
        // User needs to authorize Google first — use single-statement flow to get token
        toast.info("Please authorize Google Sheets first by exporting a single statement, then try merging again.");
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.info("Please authorize Google Sheets first by clicking 'Sheets' on any single statement.");
      } else {
        console.error('Merge Sheets error:', error);
        toast.error("Failed to create merged Google Sheet");
      }
    } finally {
      setMerging(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleDeleteStatement = async (statementId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/tax-helper/statement/${statementId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      // Remove from local state
      setStatements(statements.filter(s => s.id !== statementId));
      
      // If this was the selected statement, clear selection
      if (selectedStatement && selectedStatement.id === statementId) {
        setSelectedStatement(null);
        setTaxSummary(null);
        setExpandedCategories({});
      }
      
      toast.success("Statement deleted successfully!");
      setDeleteConfirm(null);
      
    } catch (error) {
      console.error('Delete error:', error);
      toast.error("Failed to delete statement");
      setDeleteConfirm(null);
    }
  };

  const handleDeleteAllStatements = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API}/tax-helper/statements/all`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      // Clear local state
      setStatements([]);
      setSelectedStatement(null);
      setTaxSummary(null);
      setExpandedCategories({});
      
      const deletedCount = response.data.deleted_count || 0;
      toast.success(`Successfully deleted ${deletedCount} statements!`);
      setShowDeleteAllConfirm(false);
      
    } catch (error) {
      console.error('Delete all error:', error);
      toast.error("Failed to delete statements");
      setShowDeleteAllConfirm(false);
    }
  };

  // Filter and sort statements
  const filteredAndSortedStatements = statements
    .filter(statement => 
      statement.filename.toLowerCase().includes(searchTerm.toLowerCase()) ||
      statement.account_number.includes(searchTerm)
    )
    .sort((a, b) => {
      switch (sortBy) {
        case 'date_desc':
          return new Date(b.processed_date) - new Date(a.processed_date);
        case 'date_asc':
          return new Date(a.processed_date) - new Date(b.processed_date);
        case 'filename':
          return a.filename.localeCompare(b.filename);
        case 'amount_desc':
          return b.ending_balance - a.ending_balance;
        case 'amount_asc':
          return a.ending_balance - b.ending_balance;
        default:
          return 0;
      }
    });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md shadow-sm border-b sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Dashboard
              </Button>
              <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                <Calculator className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-800">Previous Statements</h1>
                <p className="text-sm text-slate-600">Review and export your processed statements</p>
              </div>
            </div>

            {/* Desktop User Menu */}
            <div className="hidden md:flex items-center space-x-4">
              <div className="flex items-center space-x-3 px-4 py-2 bg-blue-50 rounded-lg">
                <User className="w-5 h-5 text-blue-600" />
                <div className="text-sm">
                  <p className="font-medium text-slate-800">{user?.username || 'User'}</p>
                  <p className="text-blue-600">
                    {formatCurrency(user?.credit_balance || 0)} credits
                  </p>
                </div>
              </div>

              {user?.is_admin && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/admin')}
                  className="flex items-center gap-2"
                >
                  <Crown className="w-4 h-4" />
                  Admin Panel
                </Button>
              )}

              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/my-rules')}
                className="flex items-center gap-2 relative"
                data-testid="my-rules-nav"
              >
                <Sparkles className="w-4 h-4" />
                My Rules
                {suggestionCount > 0 && (
                  <span
                    className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-indigo-600 text-white text-xs font-semibold leading-none"
                    data-testid="my-rules-badge"
                    title={`${suggestionCount} suggested rule${suggestionCount === 1 ? '' : 's'} ready to review`}
                  >
                    {suggestionCount}
                  </span>
                )}
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <Button
              variant="outline"
              size="sm"
              className="md:hidden"
              onClick={() => setShowMobileMenu(!showMobileMenu)}
            >
              <Menu className="w-5 h-5" />
            </Button>
          </div>

          {/* Mobile Menu */}
          {showMobileMenu && (
            <div className="md:hidden mt-4 pt-4 border-t space-y-2">
              <div className="flex items-center space-x-3 px-4 py-2 bg-blue-50 rounded-lg">
                <User className="w-5 h-5 text-blue-600" />
                <div className="text-sm">
                  <p className="font-medium text-slate-800">{user?.username || 'User'}</p>
                  <p className="text-blue-600">
                    {formatCurrency(user?.credit_balance || 0)} credits
                  </p>
                </div>
              </div>

              {user?.is_admin && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/admin')}
                  className="w-full justify-start"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Admin Panel
                </Button>
              )}

              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="w-full justify-start"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          )}
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8">
        {!selectedStatement ? (
          <>
            {/* Search and Filter Section */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row gap-4 items-center">
                  <div className="flex-1 relative">
                    <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search by filename or account number..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Filter className="w-5 h-5 text-slate-400" />
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="date_desc">Latest First</option>
                      <option value="date_asc">Oldest First</option>
                      <option value="filename">Filename A-Z</option>
                      <option value="amount_desc">Highest Balance</option>
                      <option value="amount_asc">Lowest Balance</option>
                    </select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Statements List */}
            {loading ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <div className="space-y-4">
                    <div className="w-16 h-16 bg-slate-200 rounded-full animate-pulse mx-auto"></div>
                    <p className="text-slate-600">Loading your statements...</p>
                  </div>
                </CardContent>
              </Card>
            ) : filteredAndSortedStatements.length > 0 ? (
              <div className="grid gap-4">
                <div className="mb-4 flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={selectedIds.size === filteredAndSortedStatements.length && filteredAndSortedStatements.length > 0}
                      onCheckedChange={toggleSelectAll}
                      data-testid="select-all-checkbox"
                    />
                    <p className="text-sm text-slate-600">
                      {selectedIds.size > 0
                        ? `${selectedIds.size} of ${filteredAndSortedStatements.length} selected`
                        : `Showing ${filteredAndSortedStatements.length} of ${statements.length} statements`}
                    </p>
                  </div>
                  {statements.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowDeleteAllConfirm(true)}
                      className="flex items-center gap-2 text-red-600 border-red-200 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete All
                    </Button>
                  )}
                </div>

                {/* Merge Action Bar */}
                {selectedIds.size >= 2 && (
                  <div className="sticky top-0 z-10 bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between shadow-sm" data-testid="merge-action-bar">
                    <div className="flex items-center gap-2 text-blue-700">
                      <Combine className="w-5 h-5" />
                      <span className="font-medium">{selectedIds.size} statements selected for merge</span>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <Button
                        onClick={() => setShowSaveMergeDialog(true)}
                        disabled={merging || savingMerge}
                        className="bg-emerald-600 hover:bg-emerald-700"
                        data-testid="save-as-ytd-btn"
                      >
                        <Layers className="w-4 h-4 mr-2" />
                        Save as Year-to-Date
                      </Button>
                      <Button
                        onClick={handleMergeExcel}
                        disabled={merging}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid="merge-excel-btn"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        {merging ? "Creating..." : "Merge to Excel"}
                      </Button>
                      <Button
                        onClick={handleMergeGoogleSheets}
                        disabled={merging}
                        variant="outline"
                        className="border-green-600 text-green-700 hover:bg-green-50"
                        data-testid="merge-sheets-btn"
                      >
                        <FileSpreadsheet className="w-4 h-4 mr-2" />
                        {merging ? "Creating..." : "Merge to Sheets"}
                      </Button>
                      <Button
                        onClick={() => setSelectedIds(new Set())}
                        variant="ghost"
                        size="sm"
                        className="text-slate-500"
                      >
                        Clear
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Smart YTD presets — quick one-click YTD/quarter save */}
                {ytdPresets.length > 0 && (
                  <Card className="mb-4 border-emerald-200 bg-gradient-to-br from-emerald-50/60 to-white" data-testid="ytd-presets-panel">
                    <CardContent className="p-4">
                      <div className="flex flex-wrap items-center gap-3">
                        <div className="flex items-center gap-2 mr-2">
                          <Layers className="w-4 h-4 text-emerald-600" />
                          <span className="text-sm font-semibold text-slate-800">Quick save</span>
                          <span className="text-xs text-slate-500">— one click to save a YTD or quarter</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {ytdPresets.map(p => (
                            <Button
                              key={p.key}
                              variant="outline"
                              size="sm"
                              className="border-emerald-300 text-emerald-700 hover:bg-emerald-100 gap-1.5 h-auto py-1.5"
                              onClick={() => applyYtdPreset(p)}
                              data-testid={`ytd-preset-${p.key}`}
                              title={`Save ${p.name} (${p.subLabel})`}
                            >
                              <span className="font-medium">{p.label}</span>
                              <span className="text-xs text-emerald-600/80">· {p.subLabel}</span>
                            </Button>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {filteredAndSortedStatements.map((statement) => (
                  <Card
                    key={statement.id}
                    className={`hover:shadow-md transition-shadow cursor-pointer ${selectedIds.has(statement.id) ? 'ring-2 ring-blue-400 bg-blue-50/30' : ''}`}
                    onClick={() => handleSelectStatement(statement)}
                  >
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <Checkbox
                              checked={selectedIds.has(statement.id)}
                              onCheckedChange={() => toggleSelect(statement.id)}
                              onClick={(e) => e.stopPropagation()}
                              data-testid={`select-stmt-${statement.id}`}
                            />
                            <FileSpreadsheet className="w-6 h-6 text-blue-600" />
                            <div>
                              <h3 className="font-semibold text-slate-800">{statement.filename}</h3>
                              <p className="text-sm text-slate-500">
                                Processed on {new Date(statement.processed_date).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                            <div>
                              <p className="text-xs text-slate-500 uppercase tracking-wide">Account</p>
                              <p className="font-medium">****{statement.account_number.slice(-4)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500 uppercase tracking-wide">Period</p>
                              <p className="font-medium text-sm">
                                {statement.statement_period_start} - {statement.statement_period_end}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500 uppercase tracking-wide">Ending Balance</p>
                              <p className="font-medium text-green-600">
                                {formatCurrency(statement.ending_balance)}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500 uppercase tracking-wide">Transactions</p>
                              <p className="font-medium">{statement.transactions.length}</p>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className="text-xs">
                              <Receipt className="w-3 h-3 mr-1" />
                              {statement.pages_processed} pages
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              <DollarSign className="w-3 h-3 mr-1" />
                              {formatCurrency(statement.cost_breakdown?.total_cost || 0)} cost
                            </Badge>
                            {(() => {
                              const u = getUncategorizedStats(statement);
                              if (u.count === 0) return null;
                              return (
                                <Badge
                                  variant="outline"
                                  className="text-xs border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100"
                                  data-testid={`uncategorized-review-${statement.id}`}
                                  title="Business expenses without a mapped category. Click the card to review."
                                >
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                  Review: {u.count} uncategorized · {formatCurrency(u.total)}
                                </Badge>
                              );
                            })()}
                          </div>
                        </div>

                        <div className="ml-4 flex flex-col gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleExportExcel(statement.id);
                            }}
                            className="flex items-center gap-2"
                          >
                            <Download className="w-4 h-4" />
                            Excel
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleExportGoogleSheets(statement.id);
                            }}
                            className="flex items-center gap-2"
                            data-testid={`export-csv-${statement.id}`}
                          >
                            <FileSpreadsheet className="w-4 h-4" />
                            Sheets
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteConfirm(statement.id);
                            }}
                            className="flex items-center gap-2 text-red-600 border-red-200 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="p-8 text-center">
                  <div className="space-y-4">
                    <FileSpreadsheet className="w-16 h-16 text-slate-300 mx-auto" />
                    <div>
                      <h3 className="text-lg font-medium text-slate-600 mb-2">No statements found</h3>
                      <p className="text-slate-500">
                        {searchTerm ? 
                          `No statements match "${searchTerm}". Try adjusting your search.` :
                          "You haven't processed any bank statements yet."
                        }
                      </p>
                    </div>
                    <Button onClick={() => navigate('/dashboard')} className="mt-4">
                      Process Your First Statement
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          /* Statement Details View */
          <div className="space-y-6">
            {/* Back Button and Statement Info */}
            <div className="flex justify-between items-start">
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedStatement(null);
                  setTaxSummary(null);
                  setExpandedCategories({});
                }}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to List
              </Button>
              
              <div className="text-right">
                <h2 className="text-xl font-bold text-slate-800">{selectedStatement.filename}</h2>
                <p className="text-slate-600">
                  Account ****{selectedStatement.account_number.slice(-4)} | 
                  {selectedStatement.statement_period_start} - {selectedStatement.statement_period_end}
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3">
              <Button
                onClick={() => handleExportExcel(selectedStatement.id)}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
              >
                <Download className="w-4 h-4" />
                Export Excel
              </Button>
              <Button
                onClick={() => handleExportGoogleSheets(selectedStatement.id)}
                variant="outline"
                className="flex items-center gap-2 border-green-600 text-green-700 hover:bg-green-50"
                data-testid="detail-export-csv-btn"
              >
                <FileSpreadsheet className="w-4 h-4" />
                Google Sheets
              </Button>
            </div>

            {taxSummary && (
              <Tabs defaultValue="summary" className="w-full">
                <TabsList className="grid w-full grid-cols-5">
                  <TabsTrigger value="summary" className="flex items-center gap-2">
                    <PieChart className="w-4 h-4" />
                    Summary
                  </TabsTrigger>
                  <TabsTrigger value="transactions" className="flex items-center gap-2">
                    <Receipt className="w-4 h-4" />
                    Transactions
                  </TabsTrigger>
                  <TabsTrigger value="categories" className="flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" />
                    Categories
                  </TabsTrigger>
                  <TabsTrigger value="profit-loss" className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    P&L
                  </TabsTrigger>
                  <TabsTrigger value="schedule-c" className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Schedule C
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="summary" className="space-y-6">
                  {(() => {
                    const u = getUncategorizedStats(selectedStatement);
                    if (u.count === 0) return null;
                    const uncategorizedTxns = (selectedStatement?.transactions || []).filter(
                      tx => tx.is_business_expense && mappedKeys && !mappedKeys.has(tx.category)
                    );
                    return (
                      <Card className="border-amber-300 bg-amber-50" data-testid="uncategorized-review-card">
                        <CardHeader className="pb-3">
                          <CardTitle className="flex items-center gap-2 text-amber-900 text-base">
                            <AlertTriangle className="w-5 h-5" />
                            Uncategorized Review — {u.count} {u.count === 1 ? 'item' : 'items'} · {formatCurrency(u.total)}
                          </CardTitle>
                          <CardDescription className="text-amber-800">
                            These business expenses don't match any export bucket and will land in the "Uncategorized Business Expenses" section of your export. Review them before exporting.
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="max-h-60 overflow-y-auto rounded border border-amber-200 bg-white">
                            <table className="w-full text-sm">
                              <thead className="bg-amber-100/60 sticky top-0">
                                <tr>
                                  <th className="text-left px-3 py-2 font-medium">Date</th>
                                  <th className="text-left px-3 py-2 font-medium">Description</th>
                                  <th className="text-right px-3 py-2 font-medium">Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {uncategorizedTxns.map((tx, idx) => (
                                  <tr key={idx} className="border-t border-amber-100">
                                    <td className="px-3 py-2 whitespace-nowrap text-slate-700">{tx.date}</td>
                                    <td className="px-3 py-2 text-slate-800">{tx.description}</td>
                                    <td className="px-3 py-2 text-right font-medium tabular-nums">{formatCurrency(tx.amount)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })()}

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-medium text-slate-600">Total Income</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-green-600">
                          {formatCurrency(taxSummary.total_income)}
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-medium text-slate-600">Total Business Expenses</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-red-600">
                          {formatCurrency(taxSummary.total_business_expenses)}
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-medium text-slate-600">Net Profit</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-blue-600">
                          {formatCurrency(taxSummary.total_income - taxSummary.total_business_expenses)}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card>
                      <CardHeader>
                        <CardTitle>Expense Categories</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="h-64">
                          {Object.keys(taxSummary.expense_categories).length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                              <RechartsPieChart>
                                <PieChart
                                  data={Object.entries(taxSummary.expense_categories).map(([category, amount]) => ({
                                    name: category,
                                    value: amount
                                  }))}
                                  cx="50%"
                                  cy="50%"
                                  outerRadius={80}
                                  dataKey="value"
                                >
                                  {Object.entries(taxSummary.expense_categories).map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                  ))}
                                </PieChart>
                                <Tooltip formatter={(value) => formatCurrency(value)} />
                                <Legend />
                              </RechartsPieChart>
                            </ResponsiveContainer>
                          ) : (
                            <div className="flex items-center justify-center h-full text-slate-500">
                              No expense categories found
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle>Balance Summary</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                            <span className="font-medium text-blue-800">Beginning Balance</span>
                            <span className="font-bold text-blue-600">{formatCurrency(selectedStatement.beginning_balance || 0)}</span>
                          </div>
                          <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                            <span className="font-medium text-purple-800">Ending Balance</span>
                            <span className="font-bold text-purple-600">{formatCurrency(selectedStatement.ending_balance || 0)}</span>
                          </div>
                          <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                            <span className="font-medium text-slate-800">Balance Change</span>
                            <span className={`font-bold ${
                              (selectedStatement.ending_balance || 0) >= (selectedStatement.beginning_balance || 0) 
                                ? 'text-green-600' 
                                : 'text-red-600'
                            }`}>
                              {formatCurrency((selectedStatement.ending_balance || 0) - (selectedStatement.beginning_balance || 0))}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle>Transaction Value Summary</CardTitle>
                        <CardDescription>Total dollar amounts by transaction type</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {/* Combined Deposits & Credits to match bank statement format */}
                          <div className="flex justify-between items-center p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-2 border-green-200">
                            <div>
                              <span className="font-semibold text-green-900">Deposits & Credits</span>
                              <p className="text-xs text-green-700 mt-1">
                                Total from bank statement summary (matches PDF total)
                              </p>
                            </div>
                            <span className="font-bold text-2xl text-green-700">
                              {formatCurrency(selectedStatement.deposits_credits || 
                                (selectedStatement.deposits || 0) + (selectedStatement.credits || 0))}
                            </span>
                          </div>
                          
                          <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                            <div>
                              <span className="font-medium text-red-800">Withdrawals</span>
                              <p className="text-xs text-red-600">Total value of withdrawal transactions</p>
                            </div>
                            <span className="font-bold text-red-600">
                              {formatCurrency(selectedStatement.withdrawals || 0)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                            <div>
                              <span className="font-medium text-orange-800">Fees</span>
                              <p className="text-xs text-orange-600">Total value of fee transactions</p>
                            </div>
                            <span className="font-bold text-orange-600">
                              {formatCurrency(selectedStatement.fees || 0)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                            <div>
                              <span className="font-medium text-blue-800">Automatic Transfers</span>
                              <p className="text-xs text-blue-600">Total value of automatic transfer transactions</p>
                            </div>
                            <span className="font-bold text-blue-600">
                              {formatCurrency(selectedStatement.automatic_transfers || 0)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center p-3 bg-indigo-50 rounded-lg">
                            <div>
                              <span className="font-medium text-indigo-800">Checks</span>
                              <p className="text-xs text-indigo-600">Total value of check transactions</p>
                            </div>
                            <span className="font-bold text-indigo-600">
                              {formatCurrency(selectedStatement.checks || 0)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                            <div>
                              <span className="font-medium text-purple-800">Transfers</span>
                              <p className="text-xs text-purple-600">Internal account transfers (EB to/from Checking, Savings, etc.)</p>
                            </div>
                            <span className="font-bold text-purple-600">
                              {formatCurrency(selectedStatement.transfers || 0)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center p-3 bg-slate-100 rounded-lg border-t-2 border-slate-300">
                            <div>
                              <span className="font-medium text-slate-800">Total Transactions</span>
                              <p className="text-xs text-slate-600">All individual transactions (includes all above categories)</p>
                            </div>
                            <span className="font-bold text-slate-600">{selectedStatement.transactions.length}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                <TabsContent value="transactions" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Transaction Details</CardTitle>
                      <CardDescription>All transactions from your bank statement</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {selectedStatement.transactions.length > 0 ? (
                        <div className="space-y-2 max-h-96 overflow-y-auto">
                          {selectedStatement.transactions.map((transaction, index) => (
                            <div key={index} className="flex justify-between items-center p-3 border rounded-lg hover:bg-slate-50">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-sm font-medium">{transaction.date}</span>
                                  <Badge 
                                    variant={transaction.transaction_type === 'credit' ? 'default' : 'secondary'}
                                    className="text-xs"
                                  >
                                    {transaction.transaction_type}
                                  </Badge>
                                  {transaction.is_business_expense && (
                                    <Badge variant="outline" className="text-xs">
                                      Business
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-slate-600">{transaction.description}</p>
                                {transaction.category && (
                                  <p className="text-xs text-slate-500 mt-1">
                                    {transaction.category} {transaction.subcategory && `• ${transaction.subcategory}`}
                                  </p>
                                )}
                              </div>
                              <div className="text-right">
                                <div className={`font-medium ${
                                  transaction.transaction_type === 'credit' ? 'text-green-600' : 'text-red-600'
                                }`}>
                                  {transaction.transaction_type === 'credit' ? '+' : '-'}{formatCurrency(transaction.amount)}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-slate-500">
                          No transactions found in this statement
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="categories" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                          <CardTitle>Expense Categories Breakdown</CardTitle>
                          <CardDescription>
                            Click any category to expand. Drag transactions between categories to re-tag them, or use checkboxes for bulk moves.
                          </CardDescription>
                        </div>
                        <label
                          className="flex items-center gap-2 cursor-pointer select-none px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
                          data-testid="group-identical-toggle"
                          title="Collapse rows that share the same description (case-insensitive) into one row with the count and total"
                        >
                          <input
                            type="checkbox"
                            checked={groupIdentical}
                            onChange={(e) => setGroupIdentical(e.target.checked)}
                            className="rounded border-slate-400 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span className="text-xs font-medium text-slate-700 whitespace-nowrap">Group identical</span>
                        </label>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {/* Undo + History row */}
                      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={!moveHistory.length || recategorizing}
                          onClick={undoLastMove}
                          data-testid="undo-last-move"
                          className="gap-1.5"
                        >
                          <Undo2 className="w-4 h-4" />
                          Undo last move
                          {moveHistory.length > 0 && (
                            <span className="ml-1 text-xs text-slate-500">({moveHistory.length})</span>
                          )}
                        </Button>
                        {moveHistory.length > 0 && (
                          <details className="text-sm">
                            <summary className="cursor-pointer text-slate-600 hover:text-slate-900 select-none">
                              Recent moves ({moveHistory.length})
                            </summary>
                            <div className="mt-2 max-w-md max-h-40 overflow-y-auto rounded border border-slate-200 bg-slate-50 p-2 text-xs space-y-1.5" data-testid="move-history-panel">
                              {[...moveHistory].reverse().map((h) => {
                                const count = Object.keys(h.previousByTxId).length;
                                const time = new Date(h.timestamp).toLocaleTimeString();
                                return (
                                  <div key={h.id} className="flex items-center gap-2">
                                    <span className="text-slate-400 tabular-nums">{time}</span>
                                    <span className="text-slate-700">
                                      {count} txn{count === 1 ? '' : 's'} → <span className="font-medium">{h.newCategory.replace(/_/g, ' ')}</span>
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </details>
                        )}
                      </div>

                      {/* Global bulk-move action bar (visible whenever any checkboxes are checked) */}
                      {selectedTxIds.size > 0 && (
                        <div
                          className="sticky top-2 z-10 mb-4 p-3 rounded-lg border border-blue-300 bg-blue-50 flex flex-wrap items-center gap-3 shadow-sm"
                          data-testid="bulk-move-bar"
                        >
                          <span className="font-medium text-blue-900 text-sm">
                            {selectedTxIds.size} selected
                          </span>
                          <select
                            value={bulkMoveCategory}
                            onChange={(e) => setBulkMoveCategory(e.target.value)}
                            className="flex-1 min-w-[200px] px-3 py-1.5 text-sm rounded border border-blue-300 bg-white"
                            data-testid="bulk-move-select"
                          >
                            <option value="">Move to…</option>
                            {allCategories.map(c => (
                              <option key={c.key} value={c.key}>{c.label}</option>
                            ))}
                          </select>
                          <Button
                            size="sm"
                            disabled={!bulkMoveCategory || recategorizing}
                            onClick={() => applyRecategorize(Array.from(selectedTxIds), bulkMoveCategory)}
                            data-testid="bulk-move-apply"
                          >
                            Apply
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setSelectedTxIds(new Set()); setBulkMoveCategory(''); }}
                          >
                            Clear
                          </Button>
                        </div>
                      )}

                      {(() => {
                        // Group transactions by their current category. Include
                        // every known category (even empty) as a drop target so
                        // users can move a txn into a brand-new bucket.
                        const businessTxns = (selectedStatement?.transactions || []).filter(t => t.is_business_expense);
                        const knownKeys = new Set(allCategories.filter(c => c.is_business_expense).map(c => c.key));
                        const byCat = {};
                        const uncategorizedTxns = [];
                        for (const t of businessTxns) {
                          const k = t.category;
                          if (!k || !knownKeys.has(k)) {
                            uncategorizedTxns.push(t);
                          } else {
                            if (!byCat[k]) byCat[k] = [];
                            byCat[k].push(t);
                          }
                        }
                        // Build the rendered list: all known categories sorted by current total desc.
                        const knownRows = allCategories
                          .filter(c => c.is_business_expense)
                          .map(c => {
                            const txns = byCat[c.key] || [];
                            const total = txns.reduce((s, t) => s + (Number(t.amount) || 0), 0);
                            return { key: c.key, label: c.label, txns, total, schedule_c_line: c.schedule_c_line, description: c.description, commonly_missed: c.commonly_missed, isUncategorized: false };
                          })
                          .sort((a, b) => b.total - a.total);

                        // Synthetic "Uncategorized" row pinned at the top whenever there's anything in it.
                        const uncategorizedTotal = uncategorizedTxns.reduce((s, t) => s + (Number(t.amount) || 0), 0);
                        const uncategorizedRow = {
                          key: '__uncategorized__',
                          label: 'Uncategorized Transactions',
                          txns: uncategorizedTxns,
                          total: uncategorizedTotal,
                          schedule_c_line: null,
                          description: "Business expenses that the engine couldn't confidently categorize. Drag each into the right bucket — and consider saving a rule so future statements auto-classify them.",
                          commonly_missed: false,
                          isUncategorized: true,
                        };
                        const rows = uncategorizedTxns.length > 0
                          ? [uncategorizedRow, ...knownRows]
                          : knownRows;

                        if (rows.length === 0) {
                          return <div className="text-center py-8 text-slate-500">No expense categories available</div>;
                        }

                        return (
                          <div className="space-y-2">
                            {rows.map(({ key, label, txns, total, isUncategorized, description, schedule_c_line, commonly_missed }) => {
                              const isExpanded = expandedCategories[key];
                              // The synthetic Uncategorized row is NOT a drop target — you can
                              // only drag OUT of it into a real category. Drop attempts on it
                              // are ignored (preventDefault is skipped).
                              const isDropTarget = !isUncategorized && dropTargetCategory === key;
                              const headerBg = isUncategorized
                                ? 'bg-amber-50 hover:bg-amber-100 border-amber-200'
                                : 'bg-slate-50 hover:bg-slate-100';
                              return (
                                <div
                                  key={key}
                                  className={`border rounded-lg overflow-hidden transition-all ${isUncategorized ? 'border-amber-300' : ''} ${isDropTarget ? 'ring-2 ring-blue-500 bg-blue-50/60' : ''}`}
                                  onDragOver={(e) => {
                                    if (isUncategorized) return; // don't allow dropping back into uncategorized
                                    e.preventDefault();
                                    setDropTargetCategory(key);
                                  }}
                                  onDragLeave={() => { if (dropTargetCategory === key) setDropTargetCategory(null); }}
                                  onDrop={(e) => {
                                    if (isUncategorized) return;
                                    e.preventDefault();
                                    setDropTargetCategory(null);
                                    const ids = draggedTxIds || [];
                                    if (ids.length) applyRecategorize(ids, key);
                                    setDraggedTxIds(null);
                                  }}
                                  data-testid={`category-row-${key}`}
                                >
                                  <button
                                    onClick={() => toggleCategory(key)}
                                    className={`w-full flex justify-between items-center p-4 transition-colors cursor-pointer ${headerBg}`}
                                  >
                                    <div className="flex items-center gap-3">
                                      {isUncategorized && <AlertTriangle className="w-4 h-4 text-amber-600" />}
                                      {isExpanded ? <ChevronUp className="w-5 h-5 text-slate-600" /> : <ChevronDown className="w-5 h-5 text-slate-600" />}
                                      <div className="text-left">
                                        <div className="flex items-center gap-2">
                                          <p className={`font-semibold ${isUncategorized ? 'text-amber-900' : 'text-slate-900'}`}>{label}</p>
                                          {commonly_missed && (
                                            <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200" title="Commonly missed deduction">
                                              Commonly Missed
                                            </span>
                                          )}
                                          {(description || schedule_c_line) && (
                                            <span
                                              role="img"
                                              aria-label={`About ${label}`}
                                              onClick={(e) => e.stopPropagation()}
                                              className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-slate-200 text-slate-600 text-[10px] font-bold cursor-help hover:bg-slate-300"
                                              title={`${description || ''}${schedule_c_line ? `\n\nSchedule C Line: ${schedule_c_line}` : ''}`}
                                              data-testid={`category-info-${key}`}
                                            >
                                              i
                                            </span>
                                          )}
                                        </div>
                                        <p className={`text-xs ${isUncategorized ? 'text-amber-700' : 'text-slate-500'}`}>
                                          {txns.length} transaction{txns.length !== 1 ? 's' : ''}
                                          {isUncategorized && ' · drag into the correct category'}
                                          {!isUncategorized && schedule_c_line && ` · Schedule C Line ${schedule_c_line}`}
                                        </p>
                                      </div>
                                    </div>
                                    <span className={`font-bold text-lg ${total > 0 ? (isUncategorized ? 'text-amber-700' : 'text-red-600') : 'text-slate-400'}`}>
                                      {formatCurrency(total)}
                                    </span>
                                  </button>

                                  {isExpanded && (
                                    <div className="p-4 bg-white border-t">
                                      {txns.length > 0 ? (
                                        <div className="space-y-2">
                                          {(groupIdentical ? groupIdenticalTxns(txns) : txns.map(t => ({
                                            description: t.description,
                                            dateDisplay: t.date,
                                            amount: t.amount,
                                            ids: [t.id],
                                            count: 1,
                                            schedule_c_line: t.schedule_c_line,
                                          }))).map((entry) => {
                                            // A group is "selected" only if all its members are checked.
                                            const allChecked = entry.ids.length > 0 && entry.ids.every(id => selectedTxIds.has(id));
                                            const someChecked = entry.ids.some(id => selectedTxIds.has(id));
                                            const isMerged = entry.count > 1;
                                            const rowKey = entry.ids.join(',');
                                            return (
                                              <div
                                                key={rowKey}
                                                draggable
                                                onDragStart={(e) => {
                                                  // If the row's checked OR if user previously selected from other rows,
                                                  // drag the union. Otherwise drag this row's ids.
                                                  const ids = (allChecked || (someChecked && selectedTxIds.size > 0))
                                                    ? Array.from(new Set([...Array.from(selectedTxIds), ...entry.ids]))
                                                    : entry.ids;
                                                  setDraggedTxIds(ids);
                                                  e.dataTransfer.effectAllowed = 'move';
                                                  e.dataTransfer.setData('text/plain', ids.join(','));
                                                }}
                                                onDragEnd={() => { setDraggedTxIds(null); setDropTargetCategory(null); }}
                                                className={`flex items-start gap-3 p-3 rounded-lg border transition-colors cursor-move ${allChecked ? 'bg-blue-50 border-blue-300' : 'bg-slate-50 hover:bg-slate-100 border-transparent'}`}
                                                data-testid={`tx-row-${rowKey}`}
                                              >
                                                <Checkbox
                                                  checked={allChecked}
                                                  onCheckedChange={() => {
                                                    setSelectedTxIds(prev => {
                                                      const next = new Set(prev);
                                                      if (allChecked) {
                                                        entry.ids.forEach(id => next.delete(id));
                                                      } else {
                                                        entry.ids.forEach(id => next.add(id));
                                                      }
                                                      return next;
                                                    });
                                                  }}
                                                  onClick={(e) => e.stopPropagation()}
                                                  data-testid={`tx-checkbox-${rowKey}`}
                                                />
                                                <div className="flex-1 min-w-0">
                                                  <p className="font-medium text-slate-900">
                                                    {entry.description}
                                                    {isMerged && (
                                                      <span className="ml-2 inline-flex items-center text-xs font-semibold text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded">
                                                        ×{entry.count}
                                                      </span>
                                                    )}
                                                  </p>
                                                  <div className="flex gap-3 mt-1 text-xs text-slate-500">
                                                    <span>{entry.dateDisplay}</span>
                                                    {entry.schedule_c_line && (
                                                      <span className="text-blue-600">Schedule C Line {entry.schedule_c_line}</span>
                                                    )}
                                                  </div>
                                                </div>
                                                <span className="font-bold text-red-600 ml-4 whitespace-nowrap">
                                                  {formatCurrency(entry.amount)}
                                                </span>
                                              </div>
                                            );
                                          })}
                                          <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg border-t-2 border-red-200 mt-3">
                                            <span className="font-semibold text-slate-700">Category Total</span>
                                            <span className="font-bold text-red-700 text-lg">{formatCurrency(total)}</span>
                                          </div>
                                        </div>
                                      ) : (
                                        <p className="text-center text-slate-400 py-4 text-sm">
                                          Empty — drop transactions here to move them into {label}
                                        </p>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}

                      {/* Educational reference — non-business categories (tooltip-only) */}
                      {allCategories.some(c => !c.is_business_expense) && (
                        <div className="mt-6 pt-4 border-t border-slate-200">
                          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                            For reference (not deductible)
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                            {allCategories.filter(c => !c.is_business_expense).map(c => (
                              <div
                                key={c.key}
                                className="flex items-center gap-2 p-2 rounded border border-slate-200 bg-slate-50/60"
                                data-testid={`reference-category-${c.key}`}
                              >
                                <span className="text-sm text-slate-700">{c.label}</span>
                                {c.description && (
                                  <span
                                    role="img"
                                    aria-label={`About ${c.label}`}
                                    className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-slate-300 text-slate-700 text-[10px] font-bold cursor-help hover:bg-slate-400"
                                    title={c.description}
                                    data-testid={`category-info-${c.key}`}
                                  >
                                    i
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="schedule-c" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Schedule C Categories</CardTitle>
                      <CardDescription>Business expense categories for tax filing</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {Object.keys(taxSummary.schedule_c_categories).length > 0 ? (
                        <div className="space-y-3">
                          {Object.entries(taxSummary.schedule_c_categories).map(([category, amount]) => (
                            <div key={category} className="flex justify-between items-center p-3 border rounded-lg">
                              <span className="font-medium">{category}</span>
                              <span className="font-bold text-blue-600">{formatCurrency(amount)}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-slate-500">
                          No Schedule C categories found
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* P&L Tab */}
                <TabsContent value="profit-loss" className="space-y-6">
                  {/* Profit & Loss Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-medium text-green-700">Total Income</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-3xl font-bold text-green-700">
                          {formatCurrency(taxSummary.total_income)}
                        </p>
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-gradient-to-br from-red-50 to-rose-50 border-red-200">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-medium text-red-700">Total Expenses</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-3xl font-bold text-red-700">
                          {formatCurrency(taxSummary.total_business_expenses)}
                        </p>
                      </CardContent>
                    </Card>
                    
                    <Card className={`bg-gradient-to-br ${
                      (taxSummary.total_income - taxSummary.total_business_expenses) >= 0 
                        ? 'from-blue-50 to-indigo-50 border-blue-200' 
                        : 'from-orange-50 to-amber-50 border-orange-200'
                    }`}>
                      <CardHeader className="pb-3">
                        <CardTitle className={`text-sm font-medium ${
                          (taxSummary.total_income - taxSummary.total_business_expenses) >= 0 
                            ? 'text-blue-700' 
                            : 'text-orange-700'
                        }`}>
                          {(taxSummary.total_income - taxSummary.total_business_expenses) >= 0 ? 'Net Profit' : 'Net Loss'}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className={`text-3xl font-bold ${
                          (taxSummary.total_income - taxSummary.total_business_expenses) >= 0 
                            ? 'text-blue-700' 
                            : 'text-orange-700'
                        }`}>
                          {formatCurrency(Math.abs(taxSummary.total_income - taxSummary.total_business_expenses))}
                        </p>
                        <p className="text-sm mt-2 text-slate-600">
                          Profit Margin: {taxSummary.total_income > 0 
                            ? ((taxSummary.total_income - taxSummary.total_business_expenses) / taxSummary.total_income * 100).toFixed(1) 
                            : 0}%
                        </p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* P&L Chart */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Income vs Expenses</CardTitle>
                      <CardDescription>Visual comparison of revenue and costs</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                          data={[
                            {
                              name: 'Income',
                              amount: taxSummary.total_income,
                              fill: '#10b981'
                            },
                            {
                              name: 'Expenses',
                              amount: taxSummary.total_business_expenses,
                              fill: '#ef4444'
                            },
                            {
                              name: 'Net Profit/Loss',
                              amount: Math.abs(taxSummary.total_income - taxSummary.total_business_expenses),
                              fill: (taxSummary.total_income - taxSummary.total_business_expenses) >= 0 ? '#3b82f6' : '#f97316'
                            }
                          ]}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip formatter={(value) => formatCurrency(value)} />
                          <Bar dataKey="amount" radius={[8, 8, 0, 0]}>
                            {[
                              { fill: '#10b981' },
                              { fill: '#ef4444' },
                              { fill: (taxSummary.total_income - taxSummary.total_business_expenses) >= 0 ? '#3b82f6' : '#f97316' }
                            ].map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Income Breakdown */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-green-700">Income Breakdown</CardTitle>
                      <CardDescription>Sources of revenue</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg border border-green-200">
                          <div>
                            <p className="font-semibold text-green-900">Total Business Income</p>
                            <p className="text-sm text-green-600">All revenue transactions</p>
                          </div>
                          <p className="text-xl font-bold text-green-700">
                            {formatCurrency(taxSummary.total_income)}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Expense Breakdown */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-red-700">Expense Breakdown</CardTitle>
                      <CardDescription>Business costs by category</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {Object.keys(taxSummary.expense_categories).length > 0 ? (
                        <div className="space-y-3">
                          {Object.entries(taxSummary.expense_categories)
                            .sort(([,a], [,b]) => b - a)
                            .map(([category, amount], index) => {
                              const percentage = (amount / taxSummary.total_business_expenses * 100).toFixed(1);
                              return (
                                <div key={category} className="flex justify-between items-center p-3 bg-red-50 rounded-lg border border-red-200">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <p className="font-medium text-red-900 capitalize">
                                        {category.replace(/_/g, ' ')}
                                      </p>
                                      <Badge variant="outline" className="text-xs">
                                        {percentage}%
                                      </Badge>
                                    </div>
                                    <div className="w-full bg-red-200 rounded-full h-2 mt-2">
                                      <div 
                                        className="bg-red-600 h-2 rounded-full" 
                                        style={{ width: `${percentage}%` }}
                                      />
                                    </div>
                                  </div>
                                  <p className="text-lg font-bold text-red-700 ml-4">
                                    {formatCurrency(amount)}
                                  </p>
                                </div>
                              );
                            })}
                          <div className="flex justify-between items-center p-4 bg-gradient-to-r from-red-100 to-rose-100 rounded-lg border-2 border-red-300 mt-4">
                            <p className="font-bold text-red-900 text-lg">Total Expenses</p>
                            <p className="text-2xl font-bold text-red-700">
                              {formatCurrency(taxSummary.total_business_expenses)}
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-8 text-slate-500">
                          No expense categories found
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Net Result Summary */}
                  <Card className={`${
                    (taxSummary.total_income - taxSummary.total_business_expenses) >= 0 
                      ? 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-300' 
                      : 'bg-gradient-to-br from-orange-50 to-amber-50 border-orange-300'
                  } border-2`}>
                    <CardHeader>
                      <CardTitle className={`text-xl ${
                        (taxSummary.total_income - taxSummary.total_business_expenses) >= 0 
                          ? 'text-blue-700' 
                          : 'text-orange-700'
                      }`}>
                        Profit & Loss Summary
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <p className="text-lg font-medium text-slate-700">Income</p>
                          <p className="text-lg font-bold text-green-700">
                            +{formatCurrency(taxSummary.total_income)}
                          </p>
                        </div>
                        <div className="flex justify-between items-center">
                          <p className="text-lg font-medium text-slate-700">Expenses</p>
                          <p className="text-lg font-bold text-red-700">
                            -{formatCurrency(taxSummary.total_business_expenses)}
                          </p>
                        </div>
                        <div className="border-t-2 border-slate-300 pt-4">
                          <div className="flex justify-between items-center">
                            <p className={`text-xl font-bold ${
                              (taxSummary.total_income - taxSummary.total_business_expenses) >= 0 
                                ? 'text-blue-700' 
                                : 'text-orange-700'
                            }`}>
                              {(taxSummary.total_income - taxSummary.total_business_expenses) >= 0 ? 'Net Profit' : 'Net Loss'}
                            </p>
                            <p className={`text-3xl font-bold ${
                              (taxSummary.total_income - taxSummary.total_business_expenses) >= 0 
                                ? 'text-blue-700' 
                                : 'text-orange-700'
                            }`}>
                              {(taxSummary.total_income - taxSummary.total_business_expenses) >= 0 ? '+' : '-'}
                              {formatCurrency(Math.abs(taxSummary.total_income - taxSummary.total_business_expenses))}
                            </p>
                          </div>
                          <div className="mt-2 text-center">
                            <p className={`text-sm font-medium ${
                              (taxSummary.total_income - taxSummary.total_business_expenses) >= 0 
                                ? 'text-blue-600' 
                                : 'text-orange-600'
                            }`}>
                              Profit Margin: {taxSummary.total_income > 0 
                                ? ((taxSummary.total_income - taxSummary.total_business_expenses) / taxSummary.total_income * 100).toFixed(2) 
                                : 0}%
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

              </Tabs>
            )}
          </div>
        )}
      </div>

      {/* "Save as Year-to-Date" Dialog */}
      {showSaveMergeDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md" data-testid="save-merge-dialog">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Layers className="w-5 h-5 text-emerald-600" />
                Save as Year-to-Date Statement
              </CardTitle>
              <CardDescription>
                Combines the {selectedIds.size} selected statements into a single re-openable view.
                Edits to individual statements will stay in sync automatically.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label htmlFor="ytd-name" className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                <input
                  id="ytd-name"
                  type="text"
                  value={saveMergeName}
                  onChange={(e) => setSaveMergeName(e.target.value)}
                  placeholder="e.g. 2024 YTD, Q1 2024, January–June 2024"
                  className="w-full px-3 py-2 rounded border border-slate-300 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  autoFocus
                  data-testid="save-merge-name-input"
                  onKeyDown={(e) => { if (e.key === 'Enter' && saveMergeName.trim()) handleSaveMergedStatement(); }}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setShowSaveMergeDialog(false); setSaveMergeName(""); }}>Cancel</Button>
                <Button
                  onClick={handleSaveMergedStatement}
                  disabled={!saveMergeName.trim() || savingMerge}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="save-merge-confirm"
                >
                  {savingMerge ? "Saving…" : "Save"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* "Remember this move?" Dialog */}
      {pendingRemember && <RememberMoveDialog
        pending={pendingRemember}
        allCategories={allCategories}
        onCancel={() => setPendingRemember(null)}
        onDontAsk={() => { setDontAskRemember(true); setPendingRemember(null); }}
        onSave={(rules) => saveRememberedRules(rules)}
      />}

      {/* Single Statement Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Delete Statement
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600 mb-6">
                Are you sure you want to delete this bank statement? This action cannot be undone.
              </p>
              <div className="flex gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setDeleteConfirm(null)}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleDeleteStatement(deleteConfirm)}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Delete Statement
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete All Statements Confirmation Dialog */}
      {showDeleteAllConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Delete All Statements
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600 mb-2">
                Are you sure you want to delete <strong>all {statements.length} statements</strong>?
              </p>
              <p className="text-red-600 text-sm mb-6">
                ⚠️ This action cannot be undone and will permanently remove all your processed bank statements.
              </p>
              <div className="flex gap-3 justify-end">
                <Button 
                  variant="outline" 
                  onClick={() => setShowDeleteAllConfirm(false)}
                >
                  Cancel
                </Button>
                <Button 
                  variant="destructive"
                  onClick={handleDeleteAllStatements}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Delete All Statements
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

export default PreviousStatements;